import React from 'react';
import { REPORT } from '../print/PrintAtoms';
import { PORTFOLIO_TOKENS, toneStyle } from './tokens';
import type { PortfolioFlagship } from '../../lib/portfolioApi';

// Same briefing as the Crown Pine Site Portfolio's flagship card
// (PineCurtainIntegraPage.tsx). `?carveOut=` is load-bearing, and for a
// portfolio share token the server re-forces it and grants exactly this one
// briefing, so the viewer's own token is appended rather than a second token.
const A1_BRIEFING_PATH = '/briefing/912d47ae-b055-4716-9375-7946623b7f3b?carveOut=crownpine-blk-194-a1';

// Canonical figures for the Alternative 3 premises: the outline snapped to
// Crown Pine parcels (data/manual/2026-09-03-crownpine-a1-alt-exhibits/
// snapped-outlines.json), and the Energy Transfer 30″ line's length inside it
// (LUMBERTONETC, measured 2026-09-28). The opportunity-block figures come off
// the payload so they follow every reseed.
const PREMISES_AC = '4,101.16';
const ET_CROSSING_MI = '0.94';

const fmt = (n: number) => Math.round(n).toLocaleString('en-US');

export const A1FlagshipCard: React.FC<{ flagship: PortfolioFlagship; token?: string }> = ({ flagship, token }) => {
  const tone = toneStyle('pass');
  const href = token ? `${A1_BRIEFING_PATH}&token=${encodeURIComponent(token)}` : A1_BRIEFING_PATH;
  return (
    <div style={{
      border: `1px solid ${REPORT.rule}`,
      borderRadius: 5,
      background: REPORT.white,
      padding: '10px 12px 11px',
      marginBottom: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        <span style={{
          fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 8, fontWeight: 700,
          letterSpacing: '0.07em', textTransform: 'uppercase', whiteSpace: 'nowrap',
          color: tone.fg, background: tone.bgWash,
          border: `1px solid ${tone.border}`, borderRadius: 3, padding: '1px 5px',
        }}>
          Flagship
        </span>
        <span style={{
          fontFamily: PORTFOLIO_TOKENS.serif, fontSize: 15, color: REPORT.heading,
          flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          A1 — Crown Pine flagship
        </span>
        <span style={{ fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 8.5, color: REPORT.tertiary, whiteSpace: 'nowrap' }}>
          Angelina County
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, marginTop: 6 }}>
        <span style={{ fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 13, fontWeight: 700, color: tone.fg }}>
          {fmt(flagship.buildable_ac)}
        </span>
        <span style={{ fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 8.2, letterSpacing: '0.05em', color: REPORT.tertiary, textTransform: 'uppercase' }}>
          buildable ac across the {fmt(flagship.gross_legal_acres)}-ac opportunity block
        </span>
      </div>

      <div style={{ marginTop: 6, fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 9.2, color: REPORT.body, lineHeight: 1.6 }}>
        <div>{PREMISES_AC} ac premises · Alternative 3 outline</div>
        <div>ET Texoma 30&#8243; crosses the premises · ~{ET_CROSSING_MI} mi</div>
      </div>

      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: 'inline-block', marginTop: 9, padding: '5px 11px',
          fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 8.5, fontWeight: 700,
          letterSpacing: '0.08em', textTransform: 'uppercase', textDecoration: 'none',
          color: REPORT.white, background: REPORT.heading, borderRadius: 3,
        }}
      >
        Open A1 briefing &rarr;
      </a>
    </div>
  );
};
